import React, { useState, useEffect, useCallback } from "react";
import {
  FaPrint,
  FaSpinner,
  FaCalendarAlt,
  FaExclamationTriangle,
  FaUniversity,
  FaCoins,
  FaCheckCircle,
} from "react-icons/fa";
import apiRequest from "../services/api";
import AccountBalanceAmendModal from "../components/AccountBalanceAmendModal";
import OpeningBalanceModal from "../components/OpeningBalanceModal";
import BalanceSheetReport from "../utils/BalanceSheetReport";

export default function BalanceSheetPage() {
  const today = new Date();
  const formatDate = (date) => date.toISOString().split("T")[0];
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(today.getDate() - 7);

  const [currentUser, setCurrentUser] = useState(() => {
    const savedUser = localStorage.getItem("user");
    try {
      return savedUser ? JSON.parse(savedUser) : null;
    } catch (e) {
      return null;
    }
  });

  const currentYear = today.getFullYear();
  const [startDate, setStartDate] = useState(`${currentYear}-01-01`);

  const [endDate, setEndDate] = useState(
    `${currentYear}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(
      today.getDate()
    ).padStart(2, "0")}`
  );

  const [showPrint, setShowPrint] = useState(false);

  const periodLabel =
    startDate && endDate
      ? `${new Date(startDate).toLocaleDateString("en-GB")} – ${new Date(
          endDate
        ).toLocaleDateString("en-GB")}`
      : "";

  const [data, setData] = useState({
    assetRows: [],
    totalAssets: 0,
    liabilityRows: [],
    totalLiabilities: 0,
    equityRows: [],
    totalEquity: 0,
    totalLiabilitiesAndEquity: 0,
    equationBalanced: true,
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [openAmendment, setOpenAmendment] = useState(false);
  const [openBalance, setOpenBalance] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState("PROFIT_TAX_PAYABLE");

  const formatCurrency = (value) => {
    const num = Number(value || 0);
    return `ETB ${num.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  function getStoredUser() {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  function isOpeningBalanceDone(stored) {
    const tenant = stored?.data?.tenant ?? stored?.tenant ?? stored?.data;
    const v = tenant?.openingBalanceDone;
    return v === true || v === "true" || v === 1;
  }

  const storedUser = getStoredUser();
  const openingBalanceDone = isOpeningBalanceDone(storedUser);

  const fetchReportData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (startDate) params.append("startDate", `${startDate}T00:00:00`);
      if (endDate) params.append("endDate", `${endDate}T23:59:59`);

      const res = await apiRequest(
        `reports/balance-sheet?${params.toString()}`
      );
      const payload = res?.data || res;

      setData({
        assetRows: payload?.assetRows || [],
        totalAssets: payload?.totalAssets || 0,
        liabilityRows: payload?.liabilityRows || [],
        totalLiabilities: payload?.totalLiabilities || 0,
        equityRows: payload?.equityRows || [],
        totalEquity: payload?.totalEquity || 0,
        totalLiabilitiesAndEquity: payload?.totalLiabilitiesAndEquity || 0,
        equationBalanced: payload?.equationBalanced ?? true,
      });
    } catch (err) {
      setError(
        err?.message || "Failed to load balance sheet. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  // ========== PRINT VIEW (single component, early return) ==========
  if (showPrint) {
    return (
      <BalanceSheetReport
        data={data}
        companyName={currentUser.data?.tenant?.name}
        reportTitle="Balance Sheet"
        periodLabel={periodLabel}
        preparedBy=""
        onAfterPrint={() => setShowPrint(false)}
      />
    );
  }

  const inputCls =
    "w-full rounded-xl pl-10 pr-4 py-2 text-sm transition focus:outline-none focus:border-emerald-500/50 bg-white border border-slate-200 text-slate-800 dark:bg-slate-900/50 dark:border-white/[0.08] dark:text-slate-200";

  const card =
    "p-5 rounded-2xl border backdrop-blur-xl bg-white border-slate-200 shadow-sm dark:bg-white/[0.02] dark:border-white/[0.08] dark:shadow-lg";

  const renderRows = (rows = []) =>
    rows.map((r, i) => {
      const isIncomeTaxPayable =
        r.accountName?.toUpperCase() === "PROFIT_TAX_PAYABLE";

      return (
        <div
          key={`${r.accountId ?? r.accountName}-${i}`}
          className={`flex justify-between items-center py-2 px-2 rounded-lg ${
            isIncomeTaxPayable
              ? "hover:bg-amber-50 cursor-pointer group dark:hover:bg-amber-500/10"
              : "hover:bg-slate-50 dark:hover:bg-white/[0.02]"
          }`}
          onClick={() => {
            if (isIncomeTaxPayable) {
              setSelectedAccount(r.accountName);
              setOpenAmendment(true);
            }
          }}
          role={isIncomeTaxPayable ? "button" : undefined}
          title={isIncomeTaxPayable ? "Amend account balance" : undefined}
        >
          <span
            className={
              isIncomeTaxPayable
                ? "text-amber-700 group-hover:text-amber-600 dark:text-amber-300 dark:group-hover:text-amber-200"
                : "text-slate-600 dark:text-slate-300"
            }
          >
            {r.accountName}
          </span>

          <span className="font-mono flex items-center gap-2 text-slate-800 dark:text-slate-100">
            {formatCurrency(r.amount)}
            {isIncomeTaxPayable && (
              <span className="text-[9px] uppercase tracking-wider opacity-0 group-hover:opacity-100 transition text-amber-600 dark:text-amber-500/80">
                Edit
              </span>
            )}
          </span>
        </div>
      );
    });

  const section = (title, colorLight, colorDark, rows, total, label) => (
    <div className="rounded-2xl p-5 shadow-sm border bg-white border-slate-200 dark:bg-white/[0.02] dark:border-white/[0.08] dark:shadow-xl">
      <h3
        className={`text-sm font-bold uppercase border-b pb-2 mb-3 border-slate-100 dark:border-white/[0.05] ${colorLight} ${colorDark}`}
      >
        {title}
      </h3>
      <div className="space-y-1">{renderRows(rows)}</div>
      <div
        className={`flex justify-between mt-4 px-4 py-3 rounded-xl font-bold bg-slate-50 dark:bg-white/[0.03] ${colorLight} ${colorDark}`}
      >
        <span>{label}</span>
        <span className="font-mono">{formatCurrency(total)}</span>
      </div>
    </div>
  );

  return (
    <div className="min-h-full relative overflow-hidden font-sans p-3 md:p-6 bg-slate-50 text-slate-800 dark:bg-slate-950 dark:text-slate-100">
      <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] rounded-full pointer-events-none bg-blue-500/5 blur-[120px] dark:bg-blue-500/10" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[600px] h-[600px] rounded-full pointer-events-none bg-emerald-500/5 blur-[150px] dark:bg-emerald-500/10" />

      <div className="max-w-7xl mx-auto relative z-10 space-y-6">
        {/* HEADER */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between p-6 rounded-2xl gap-4 border shadow-sm backdrop-blur-xl bg-white border-slate-200 dark:bg-white/[0.03] dark:border-white/[0.08] dark:shadow-2xl">
          <div>
            <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900 dark:bg-gradient-to-r dark:from-white dark:via-slate-200 dark:to-slate-400 dark:bg-clip-text dark:text-transparent flex items-center gap-3">
              <FaUniversity className="text-blue-600 dark:text-blue-400" />
              Balance Sheet
            </h1>
            <p className="text-xs mt-1 text-slate-500 dark:text-slate-400">
              Assets, liabilities and owner's equity
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => setShowPrint(true)}
              disabled={loading || !!error}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl
                         bg-emerald-600 hover:bg-emerald-500
                         text-white text-xs font-bold
                         shadow-md transition
                         disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FaPrint />
              Print
            </button>

            {!openingBalanceDone && (
              <button
                type="button"
                onClick={() => setOpenBalance(true)}
                className="inline-flex items-center justify-center gap-2
                           px-4 py-2.5 rounded-xl
                           bg-amber-600 hover:bg-amber-500
                           text-white text-xs font-bold
                           shadow-md transition"
              >
                <FaCoins />
                Opening Balance
              </button>
            )}
          </div>
        </div>

        {/* DATE FILTERS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 rounded-xl border backdrop-blur-md bg-white border-slate-200 dark:bg-white/[0.02] dark:border-white/[0.05]">
          <div>
            <label className="text-xs text-slate-500 dark:text-slate-400">
              Start Date
            </label>
            <div className="relative flex items-center mt-1">
              <FaCalendarAlt className="absolute left-3 text-slate-400 dark:text-slate-500" />
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className={inputCls}
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-500 dark:text-slate-400">
              End Date
            </label>
            <div className="relative flex items-center mt-1">
              <FaCalendarAlt className="absolute left-3 text-slate-400 dark:text-slate-500" />
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className={inputCls}
              />
            </div>
          </div>
        </div>

        {/* SUMMARY CARDS */}
        {!loading && !error && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className={card}>
              <span className="text-xs uppercase font-semibold text-slate-500 dark:text-slate-400">
                Total Net Assets
              </span>
              <h2 className="text-3xl font-bold text-blue-600 dark:text-blue-400 mt-1">
                {formatCurrency(data.totalAssets)}
              </h2>
            </div>
            <div className={card}>
              <span className="text-xs uppercase font-semibold text-slate-500 dark:text-slate-400">
                Liabilities + Equity
              </span>
              <h2 className="text-3xl font-bold text-purple-600 dark:text-purple-400 mt-1">
                {formatCurrency(data.totalLiabilitiesAndEquity)}
              </h2>
            </div>
            <div
              className={`p-5 rounded-2xl border shadow-sm flex items-center justify-between ${
                data.equationBalanced
                  ? "bg-emerald-50 border-emerald-200 dark:bg-emerald-500/[0.02] dark:border-emerald-500/20"
                  : "bg-rose-50 border-rose-200 dark:bg-rose-500/[0.02] dark:border-rose-500/20"
              }`}
            >
              <div>
                <span className="text-xs uppercase font-semibold text-slate-500 dark:text-slate-400">
                  Accounting Identity
                </span>
                <h2
                  className={`text-xl font-bold mt-1 ${
                    data.equationBalanced
                      ? "text-emerald-600 dark:text-emerald-400"
                      : "text-rose-600 dark:text-rose-400"
                  }`}
                >
                  {data.equationBalanced ? "Balanced" : "Imbalance"}
                </h2>
              </div>
              {data.equationBalanced ? (
                <FaCheckCircle className="text-emerald-500 dark:text-emerald-400 text-2xl" />
              ) : (
                <FaExclamationTriangle className="text-rose-500 dark:text-rose-400 text-2xl" />
              )}
            </div>
          </div>
        )}

        {loading && (
          <div className="text-center py-12 rounded-2xl border backdrop-blur-md text-slate-500 bg-white border-slate-200 dark:text-slate-400 dark:bg-white/[0.01] dark:border-white/[0.05]">
            <FaSpinner className="animate-spin inline mr-3 text-blue-500 text-lg" />
            Loading balance sheet...
          </div>
        )}

        {error && (
          <div className="flex items-center gap-3 justify-center text-center py-8 rounded-2xl border text-rose-600 bg-rose-50 border-rose-200 dark:text-rose-400 dark:bg-rose-950/20 dark:border-rose-500/20">
            <FaExclamationTriangle />
            <span>{error}</span>
          </div>
        )}

        {!loading && !error && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {section(
              "Asset Accounts",
              "text-blue-600",
              "dark:text-blue-400",
              data.assetRows,
              data.totalAssets,
              "TOTAL ASSETS"
            )}
            <div className="space-y-6">
              {section(
                "Liabilities",
                "text-amber-600",
                "dark:text-amber-400",
                data.liabilityRows,
                data.totalLiabilities,
                "TOTAL LIABILITIES"
              )}
              {section(
                "Owner Equity",
                "text-purple-600",
                "dark:text-purple-400",
                data.equityRows,
                data.totalEquity,
                "TOTAL EQUITY"
              )}
              <div className="flex justify-between p-4 rounded-xl font-extrabold border bg-purple-50 border-purple-200 text-purple-700 dark:bg-purple-500/10 dark:border-purple-500/30 dark:text-purple-300">
                <span>TOTAL LIABILITIES & EQUITY</span>
                <span className="font-mono">
                  {formatCurrency(data.totalLiabilitiesAndEquity)}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      <OpeningBalanceModal
        isOpen={openBalance}
        onClose={() => setOpenBalance(false)}
        onSuccess={() => {
          setOpenBalance(false);
          fetchReportData();
        }}
      />

      <AccountBalanceAmendModal
        isOpen={openAmendment}
        onClose={() => setOpenAmendment(false)}
        onSuccess={() => {
          setOpenAmendment(false);
          fetchReportData();
        }}
        defaultAccountCode={selectedAccount}
      />
    </div>
  );
}
